import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import Card from '../../../shared/card/Card';
import MapView from '@arcgis/core/views/MapView';
import Map from '@arcgis/core/Map';
import { Property } from '../../../../models/Property';
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Color from '@arcgis/core/Color';
import errorLogger from '../../../../utilities/errorLogger';
import Extent from '@arcgis/core/geometry/Extent';

interface MapSectionProps {
  property: Property;
  extent: __esri.Extent;
  map: React.MutableRefObject<__esri.WebMap>;
}

export default function MapSection({ property, extent, map }: MapSectionProps) {
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef(new AbortController());
  const view = useRef(new MapView({
    map: new Map({
      basemap: "hybrid"
    }),
    extent: extent
  }));

  // Create a graphics layer to add the highlighted property geometry
  const graphicsLayer = useRef(new GraphicsLayer());

  useEffect(() => {
    const mapView = view.current; // Store view.current in a stable variable
    const layer = graphicsLayer.current; // Store graphicsLayer.current in a stable variable

    if (mapView && layer) {
        mapView.map.add(layer);
    }

    // Cleanup on component unmount
    return () => {
        if (mapView && layer) {
            mapView.map.remove(layer);
        }
    };
  }, []);



  /**
   * Adjusts zoom level for Summary page map. Map view does not update
   * to the extent of polygon due to map div constantly changing height.
   */
  const getZoomLevel = useCallback(() => {
    if (!property.geometry) return 15;

    const area = geometryEngine.geodesicArea(
        property.geometry as __esri.Polygon,
        "square-miles"
    );

    if (!area || isNaN(area)) return 15; // Default if invalid

    if (area < 0.002) return 17;
    if (area < 0.016) return 16;
    if (area < 0.04) return 15;
    if (area < 0.09) return 14;
    if (area < 0.2) return 13;
    if (area < 1) return 12;
    if (area < 2) return 11;
    return 10;
  }, [property.geometry]);

  useMemo(() => {
    const source_propertiesLayer = map.current.layers
      .find(layer => layer.title === 'Properties') as __esri.FeatureLayer;

    const duplicates: Array<__esri.Layer> = [];
    view.current.map.layers.forEach(layer => {
        if (layer.type === 'feature') {
            const fl = layer as FeatureLayer;
            if (fl.portalItem && fl.portalItem === source_propertiesLayer.portalItem) {
                duplicates.push(layer);
            }
        }
    });

    if (duplicates.length > 0) view.current.map.removeMany(duplicates);

    abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    if (property.geometry && property.geometry.extent) {
        view.current.extent = property.geometry.extent;
        view.current.zoom = getZoomLevel();  // Use the dynamic zoom level!
        view.current?.when(() => {
          view.current!.goTo(property.geometry, { signal }).catch(errorLogger);
        }).catch(console.error);
      

        // Highlight property
        const highlightSymbol = new SimpleFillSymbol({
            outline: new SimpleLineSymbol({
                color: new Color([255, 201, 7]), // lighter orange border
                width: 2
            })
        });

        const graphic = new Graphic({
            geometry: property.geometry,
            symbol: highlightSymbol
        });

        graphicsLayer.current.removeAll();
        graphicsLayer.current.add(graphic);
    } else {
        // Reset map if geometry is invalid
        const usaExtent = new Extent({
            xmin: -125.0,
            ymin: 24.396308,
            xmax: -66.93457,
            ymax: 49.384358,
            spatialReference: { wkid: 4326 }
        });
        view.current.extent = usaExtent;
        view.current.zoom = 4;
        graphicsLayer.current.removeAll();
    }
}, [map, property, getZoomLevel]);


return (
  <section className='map-section'>
    <Card>
      <div className='location-title'>Location</div>
      <div className="summary-view-container">
        <div className="summary-map-container" ref={(node) => {
          mapDiv.current = node;
          if (mapDiv.current) {
            view.current.container = mapDiv.current;
          }
        }}>
          {(!property.geometry || !property.geometry.extent) && (
            <div className="pending-overlay">
              Pending Property Boundary
            </div>
          )}
        </div>
      </div>
    </Card>
  </section>
);

}
